import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Select,
  DatePicker,
  Table,
  Tag,
  Statistic,
  Space,
  theme,
  Spin,
  message,
  Button,
  Empty,
} from "antd";
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DollarOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Summary stat card component
function SummaryStatCard({ title, value, suffix, prefix, icon, color, loading }) {
  const { token } = theme.useToken();

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>{title}</Text>
          <Statistic
            value={loading ? "—" : value}
            suffix={suffix}
            prefix={prefix}
            valueStyle={{ fontSize: 24, fontWeight: 700, color: token.colorText }}
            loading={loading}
          />
        </div>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: color || "rgba(84, 162, 204, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color: token.colorPrimary,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function DispatchSummary() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [pageSize, setPageSize] = useState(10);

  // Data states
  const [stats, setStats] = useState({
    totalDispatches: 0,
    completedDispatches: 0,
    runningDispatches: 0,
    scheduledDispatches: 0,
    totalRevenue: 0,
    totalEnergy: 0,
    avgCompletionRate: 0,
    avgPerformanceScore: 0,
  });
  const [recentDispatches, setRecentDispatches] = useState([]);
  const [dispatchByGroup, setDispatchByGroup] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [controlGroups, setControlGroups] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${authToken}` };

      // Fetch dispatch summary and control groups in parallel
      const [summaryRes, groupsRes] = await Promise.all([
        fetch("/api/dispatch/summary", { headers }),
        fetch("/api/control-groups", { headers }),
      ]);

      const summaryData = await summaryRes.json();
      const groupsData = await groupsRes.json();

      if (summaryData.success && summaryData.data) {
        setStats(summaryData.data.stats || {});
        setRecentDispatches(summaryData.data.recentDispatches || []);
        setDispatchByGroup(summaryData.data.dispatchByGroup || []);
        setStrategies(summaryData.data.strategies || []);
      }

      if (groupsData.success && groupsData.data) {
        setControlGroups(groupsData.data);
      }
    } catch (error) {
      console.error("Failed to fetch dispatch data:", error);
      message.error("Failed to load dispatch summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group options for filter
  const groupOptions = [
    { value: "all", label: "All Groups" },
    ...controlGroups.map(g => ({ value: g.id, label: g.name })),
  ];

  // Status tag config
  const getStatusConfig = (status) => {
    const config = {
      completed: { color: "success", icon: <CheckCircleOutlined />, label: "Completed" },
      running: { color: "processing", icon: <SyncOutlined spin />, label: "Running" },
      scheduled: { color: "warning", icon: <CalendarOutlined />, label: "Scheduled" },
      booking: { color: "default", icon: <ClockCircleOutlined />, label: "Booking" },
      cancelled: { color: "error", icon: <ExclamationCircleOutlined />, label: "Cancelled" },
      error: { color: "error", icon: <ExclamationCircleOutlined />, label: "Error" },
    };
    return config[status] || { color: "default", icon: <ClockCircleOutlined />, label: status };
  };

  // Dispatch type colors
  const typeColors = {
    discharge: "#f5222d",
    charge: "#52c41a",
    mixed: "#faad14",
  };

  // Columns for recent dispatches table
  const columns = [
    {
      title: "Dispatch ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (id) => <Text code style={{ fontSize: 12 }}>{id?.substring(0, 12) || "—"}</Text>,
    },
    {
      title: "Control Group",
      dataIndex: "controlGroupName",
      key: "controlGroupName",
      width: 150,
      render: (name) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: "Type",
      dataIndex: "dispatchType",
      key: "dispatchType",
      width: 100,
      render: (type) => (
        <Tag color={typeColors[type] || "default"}>
          {type === "discharge" ? "Discharge" : type === "charge" ? "Charge" : type}
        </Tag>
      ),
    },
    {
      title: "Strategy",
      dataIndex: "strategyName",
      key: "strategyName",
      width: 140,
      render: (name) => name || "—",
    },
    {
      title: "Devices",
      key: "devices",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Text>
          {record.participatingDevices || 0}/{record.totalDevices || 0}
        </Text>
      ),
    },
    {
      title: "Energy (kWh)",
      dataIndex: "actualEnergyKwh",
      key: "energy",
      width: 110,
      align: "right",
      render: (val) => val?.toFixed(2) || "—",
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      width: 100,
      align: "right",
      render: (val) => val ? `$${val.toFixed(2)}` : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color} icon={config.icon}>{config.label}</Tag>;
      },
    },
    {
      title: "Time",
      dataIndex: "scheduledStart",
      key: "time",
      width: 160,
      render: (time) => time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "—",
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<RightOutlined />}
          onClick={() => navigate(`/dispatch/current/${record.id}`)}
          style={{ padding: 0 }}
        >
          View
        </Button>
      ),
    },
  ];

  // Chart data - dispatch by group
  const chartColors = ["#54A2CC", "#05a677", "#faad14", "#f5a623", "#eb2f96", "#13c2c2"];
  const groupChartData = dispatchByGroup.map((g, i) => ({
    name: g.name,
    dispatches: g.dispatchCount,
    revenue: g.totalRevenue,
    energy: g.totalEnergy,
    fill: chartColors[i % chartColors.length],
  }));

  // Status breakdown for pie chart
  const statusData = [
    { name: "Completed", value: stats.completedDispatches, color: "#52c41a" },
    { name: "Running", value: stats.runningDispatches, color: "#1890ff" },
    { name: "Scheduled", value: stats.scheduledDispatches, color: "#faad14" },
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <PageLayout active="dispatchSummary">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="dispatchSummary">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Dispatch Summary
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Overview of dispatch operations, performance metrics, and revenue
        </Text>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
              Control Group
            </Text>
            <Select
              value={selectedGroup}
              onChange={setSelectedGroup}
              options={groupOptions}
              style={{ width: 200 }}
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: 280 }}
            />
          </div>
          <div style={{ paddingTop: 22 }}>
            <Button type="primary" icon={<SyncOutlined />} onClick={fetchData}>
              Refresh
            </Button>
          </div>
        </Space>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Dispatches"
            value={stats.totalDispatches}
            icon={<ThunderboltOutlined />}
            color="rgba(84, 162, 204, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Completed"
            value={stats.completedDispatches}
            icon={<CheckCircleOutlined />}
            color="rgba(82, 196, 26, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Running"
            value={stats.runningDispatches}
            icon={<PlayCircleOutlined />}
            color="rgba(24, 144, 255, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Scheduled"
            value={stats.scheduledDispatches}
            icon={<CalendarOutlined />}
            color="rgba(250, 173, 20, 0.1)"
            loading={loading}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Revenue"
            value={stats.totalRevenue.toFixed(2)}
            prefix="$"
            icon={<DollarOutlined />}
            color="rgba(5, 166, 119, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Energy"
            value={stats.totalEnergy.toFixed(2)}
            suffix="kWh"
            icon={<ThunderboltOutlined />}
            color="rgba(250, 173, 20, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Avg Completion Rate"
            value={stats.avgCompletionRate.toFixed(1)}
            suffix="%"
            icon={<CheckCircleOutlined />}
            color="rgba(82, 196, 26, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Avg Performance"
            value={stats.avgPerformanceScore.toFixed(1)}
            suffix="/100"
            icon={<ThunderboltOutlined />}
            color="rgba(245, 166, 35, 0.1)"
            loading={loading}
          />
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={
              <Space>
                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                <Text strong>Dispatch by Control Group</Text>
              </Space>
            }
          >
            {groupChartData.length > 0 ? (
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={groupChartData} margin={{ top: 10, right: 30, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === "revenue" ? `$${value.toFixed(2)}` : value,
                        name === "revenue" ? "Revenue" : name === "dispatches" ? "Dispatches" : "Energy (kWh)"
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="dispatches" name="Dispatches" fill="#54A2CC" />
                    <Bar dataKey="revenue" name="Revenue ($)" fill="#05a677" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty description="No dispatch data available" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={
              <Space>
                <CheckCircleOutlined style={{ color: token.colorPrimary }} />
                <Text strong>Dispatch Status</Text>
              </Space>
            }
          >
            {statusData.length > 0 ? (
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty description="No status data" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Dispatches Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <ClockCircleOutlined style={{ color: token.colorPrimary }} />
            <Text strong>Recent Dispatches</Text>
          </Space>
        }
        extra={
          <Button type="link" onClick={() => navigate("/dispatch/current")}>
            View All
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={recentDispatches}
          rowKey="id"
          pagination={makePagination(pageSize, setPageSize)}
          scroll={{ x: 1200 }}
          locale={{ emptyText: "No dispatches found" }}
        />
      </Card>
    </PageLayout>
  );
}
